/**
 * Push Notification Hook and Registration
 * 
 * Handles Web Push subscription registration with the backend.
 * PWA-ready for future native app integration.
 */
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Convert a base64 string to a Uint8Array for the applicationServerKey
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Custom hook for managing push notification subscriptions
 */
export function usePushNotifications() {
  const { api, user } = useAuth();
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [permission, setPermission] = useState('default');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Check if push notifications are supported
  useEffect(() => {
    const supported = 
      'serviceWorker' in navigator && 
      'PushManager' in window && 
      'Notification' in window;
    
    setIsSupported(supported);
    
    if ('Notification' in window) {
      setPermission(Notification.permission);
    }
  }, []);

  // Check current subscription status
  const checkSubscription = useCallback(async () => {
    if (!isSupported) return;

    try {
      const registration = await navigator.serviceWorker.ready;
      const existingSub = await registration.pushManager.getSubscription();
      
      setSubscription(existingSub);
      setIsSubscribed(!!existingSub);
    } catch (err) {
      console.error('Error checking subscription:', err);
    }
  }, [isSupported]);

  useEffect(() => {
    checkSubscription();
  }, [checkSubscription]);

  /**
   * Subscribe to push notifications
   */
  const subscribe = useCallback(async () => {
    if (!isSupported || !api) {
      setError('Push notifications not supported');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      // Request notification permission
      const permission = await Notification.requestPermission();
      setPermission(permission);

      if (permission !== 'granted') {
        setError('Notification permission denied');
        setLoading(false);
        return false;
      }

      // Get VAPID public key from server
      const vapidResponse = await api.get('/notifications/vapid-public-key');
      const vapidPublicKey = vapidResponse.data.publicKey;

      if (!vapidPublicKey) {
        setError('Failed to get VAPID key');
        setLoading(false);
        return false;
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;

      // Subscribe to push
      const pushSubscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
      });

      // Send subscription to server
      const subscriptionJSON = pushSubscription.toJSON();
      await api.post('/notifications/subscribe', {
        subscription: subscriptionJSON,
        platform: 'web',
        device_id: `web_${navigator.userAgent.slice(0, 50)}`,
      });

      setSubscription(pushSubscription);
      setIsSubscribed(true);
      setLoading(false);
      
      return true;
    } catch (err) {
      console.error('Push subscription error:', err);
      setError(err.message || 'Failed to subscribe');
      setLoading(false);
      return false;
    }
  }, [isSupported, api]);

  /**
   * Unsubscribe from push notifications
   */
  const unsubscribe = useCallback(async () => {
    if (!subscription || !api) return false;

    setLoading(true);
    setError(null);

    try {
      // Unsubscribe from browser
      await subscription.unsubscribe();

      // Notify server
      await api.post('/notifications/unsubscribe', {
        endpoint: subscription.endpoint,
      });

      setSubscription(null);
      setIsSubscribed(false);
      setLoading(false);
      
      return true;
    } catch (err) {
      console.error('Push unsubscribe error:', err);
      setError(err.message || 'Failed to unsubscribe');
      setLoading(false);
      return false;
    }
  }, [subscription, api]);

  return {
    isSupported,
    isSubscribed,
    permission,
    loading,
    error,
    subscribe,
    unsubscribe,
    checkSubscription,
  };
}

/**
 * Register service worker and set up push handling
 */
export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    console.log('Service Worker not supported');
    return null;
  }

  try {
    const ENABLE_PUSH_SW = false;

    if (!ENABLE_PUSH_SW) {
      return null;
    }

    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
    });

    console.log('Service Worker registered:', registration);

    await navigator.serviceWorker.ready;

    return registration;
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    return null;
  }
}

/**
 * Request notification permission
 */
export async function requestNotificationPermission() {
  if (!('Notification' in window)) {
    return 'denied';
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  if (Notification.permission === 'denied') {
    return 'denied';
  }

  return await Notification.requestPermission();
}

export default usePushNotifications;
